/**
 * CalendarItemDetailsModal
 * Modal for viewing/editing calendar items (Events and Tasks)
 */

import React, { useState } from 'react'
import { CopyableProcessNumber } from '../ui/CopyableProcessNumber'
import {
  Clock,
  MapPin,
  User,
  Tag,
  Calendar,
  Edit,
  Trash2,
  ExternalLink,
  Link as LinkIcon,
  Video,
  CheckSquare
} from 'lucide-react'
import { CalendarItem } from '../../types/calendar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  CalendarPriorityBadge,
  eventStatusLabels,
  eventTypeLabels,
  taskCategoryLabels,
  taskStatusLabels
} from './CalendarBadges'
import { useTaskDrawer } from '@/contexts/TaskDrawerContext'
import { eventService } from '../../services/calendar-service'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

import { getErrorMessage } from '../../utils/error.utils'
interface CalendarItemDetailsModalProps {
  item: CalendarItem | null
  isOpen: boolean
  onClose: () => void
  onUpdate?: () => void
}

export const CalendarItemDetailsModal: React.FC<CalendarItemDetailsModalProps> = ({
  item,
  isOpen,
  onClose,
  onUpdate
}) => {
  const taskDrawer = useTaskDrawer()
  const [deleting, setDeleting] = useState(false)

  if (!isOpen || !item) return null

  const handleEdit = () => {
    if (item.type === 'task') {
      onClose()
      taskDrawer.openTask(Number(item.id))
    }
    // TODO: edição inline para events não implementada
  }

  const handleDelete = async () => {
    if (!confirm('Tem certeza que deseja excluir este item?')) return

    try {
      setDeleting(true)

      if (item.type === 'event') {
        await eventService.delete(item.id)
        toast.success('Evento excluído com sucesso!')
      } else {
        toast.error('Exclusão de tarefas ainda não implementada aqui')
        return
      }

      onUpdate?.()
      onClose()
    } catch (error: unknown) {
      console.error('Error deleting item:', error)
      toast.error(getErrorMessage(error, 'Erro ao excluir item'))
    } finally {
      setDeleting(false)
    }
  }

  const handleViewTaskDetails = () => {
    onClose()
    taskDrawer.openTask(Number(item.id))
  }

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
    } catch {
      return dateStr
    }
  }

  const formatTime = (timeStr?: string) => {
    if (!timeStr) return null
    return timeStr
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !deleting) onClose() }}>
      <DialogContent size="lg" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {item.type === 'event' ? (
              <Calendar className="size-5 text-muted-foreground" aria-hidden="true" />
            ) : (
              <CheckSquare className="size-5 text-muted-foreground" aria-hidden="true" />
            )}
            {item.type === 'event' ? 'Detalhes do Evento' : 'Detalhes da Tarefa'}
          </DialogTitle>
          <DialogDescription className="sr-only">{item.title}</DialogDescription>
        </DialogHeader>

        <div className="flex min-w-0 flex-col gap-5">
          <div>
            <h3 className="break-words text-xl font-semibold">{item.title}</h3>
            {item.description && (
              <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{item.description}</p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {item.type === 'event' && item.eventType && (
              <Badge variant="outline">{eventTypeLabels[item.eventType]}</Badge>
            )}
            {item.type === 'event' && item.eventStatus && (
              <Badge variant="secondary">{eventStatusLabels[item.eventStatus]}</Badge>
            )}
            {item.type === 'task' && item.taskCategory && (
              <Badge variant="outline">{taskCategoryLabels[item.taskCategory]}</Badge>
            )}
            {item.type === 'task' && item.taskStatus && (
              <Badge variant="secondary">{taskStatusLabels[item.taskStatus]}</Badge>
            )}
            <CalendarPriorityBadge priority={item.priority} />
          </div>

          <div className="flex flex-col gap-3 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="size-4 text-muted-foreground" aria-hidden="true" />
              <span className="font-medium">{formatDate(item.date)}</span>
            </div>

            {item.type === 'event' && item.startTime && (
              <div className="flex items-center gap-2">
                <Clock className="size-4 text-muted-foreground" aria-hidden="true" />
                <span>
                  {formatTime(item.startTime)}
                  {item.endTime && ` - ${formatTime(item.endTime)}`}
                </span>
              </div>
            )}

            {item.allDay && <Badge variant="secondary" className="self-start">Dia inteiro</Badge>}
          </div>

          {item.type === 'event' && item.rawEvent && (
            <>
              {item.rawEvent.location && (
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="break-words font-medium">{item.rawEvent.location}</p>
                    {item.rawEvent.locationType && (
                      <p className="text-muted-foreground">{item.rawEvent.locationType}</p>
                    )}
                  </div>
                </div>
              )}

              {item.rawEvent.onlineMeetingLink && (
                <div className="flex items-center gap-2 text-sm">
                  <Video className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <a
                    href={item.rawEvent.onlineMeetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-primary underline-offset-4 hover:underline"
                  >
                    Link da Reunião Online
                    <ExternalLink className="size-4" />
                  </a>
                </div>
              )}
            </>
          )}

          {(item.clientName || item.caseNumber) && (
            <div className="flex flex-col gap-2 border-t pt-4 text-sm">
              {item.clientName && (
                <div className="flex items-center gap-2">
                  <User className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <span className="min-w-0 break-words">Cliente: {item.clientName}</span>
                </div>
              )}
              {item.caseNumber && (
                <div className="flex flex-wrap items-center gap-2">
                  <LinkIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <span>Processo:</span>
                  <CopyableProcessNumber number={item.caseNumber} variant="badge" />
                </div>
              )}
            </div>
          )}

          {item.tags && item.tags.length > 0 && (
            <div className="flex items-start gap-2">
              <Tag className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <div className="flex flex-wrap gap-1">
                {item.tags.map((tag, index) => (
                  <Badge key={index} variant="secondary">{tag}</Badge>
                ))}
              </div>
            </div>
          )}

          {item.linkedEventId && (
            <div className="rounded-lg border bg-muted/40 p-3 text-sm">
              Esta tarefa está vinculada a um evento agendado
            </div>
          )}

          {item.followUpTaskId && (
            <div className="rounded-lg border bg-muted/40 p-3 text-sm">
              Este evento tem uma tarefa de acompanhamento
            </div>
          )}

          <div className="border-t pt-4 text-xs text-muted-foreground">
            <p>Criado em: {formatDate(item.createdAt)}</p>
            <p>Atualizado em: {formatDate(item.updatedAt)}</p>
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <div>
            {item.type === 'event' && (
              <Button
                variant="outline"
                onClick={handleDelete}
                disabled={deleting}
                className="w-full text-destructive hover:text-destructive sm:w-auto"
              >
                <Trash2 className="size-4" />
                {deleting ? 'Excluindo...' : 'Excluir'}
              </Button>
            )}
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button variant="outline" onClick={onClose} disabled={deleting}>
              Fechar
            </Button>
            {item.type === 'task' ? (
              <Button onClick={handleViewTaskDetails}>
                Ver Detalhes Completos
                <ExternalLink className="size-4" />
              </Button>
            ) : (
              <Button onClick={handleEdit} disabled={deleting}>
                <Edit className="size-4" />
                Editar
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
